import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { schoolProgramAPI } from '../lib/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  School, Users, Calendar, MapPin, Phone, Mail, CheckCircle, ArrowLeft, 
  Download, ChevronLeft, ChevronRight, Briefcase, Clock, FileText, Sparkles
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SchoolRegistration = () => {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [registrationResult, setRegistrationResult] = useState(null);
  const [currentCarouselIndex, setCurrentCarouselIndex] = useState(0);
  const carouselRef = useRef(null);
  
  const [formData, setFormData] = useState({
    school_name: '',
    school_type: '',
    address: '',
    region: '',
    contact_person_name: '',
    contact_person_role: '',
    contact_email: '',
    contact_phone: '',
    number_of_students: '',
    grade_levels: [],
    program_type: '',
    preferred_session_date: '',
    preferred_session_time: '',
    preferred_days: [],
    preferred_times: '',
    pool_access: false,
    transportation_assistance: false,
    notes: '',
    terms_accepted: false,
    privacy_accepted: false
  });

  const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
  const timeSlots = [
    '8:00 AM - 10:00 AM',
    '10:00 AM - 12:00 PM',
    '12:00 PM - 2:00 PM',
    '2:00 PM - 4:00 PM',
    'Flexible'
  ];

  useEffect(() => {
    fetchConfig();
  }, []);

  // Auto-scroll carousel (slower speed)
  useEffect(() => {
    if (config?.carousel_images?.length > 1) {
      const interval = setInterval(() => {
        setCurrentCarouselIndex(prev => 
          (prev + 1) % config.carousel_images.length
        );
      }, 5000); // 5 seconds per image
      return () => clearInterval(interval);
    }
  }, [config?.carousel_images?.length]);

  const fetchConfig = async () => {
    try {
      const response = await schoolProgramAPI.getConfig();
      setConfig(response.data);
    } catch (error) {
      console.error('Failed to load configuration:', error);
      toast.error('Failed to load program configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (name, checked) => {
    setFormData(prev => ({ ...prev, [name]: checked }));
  };

  const handleMultiSelectChange = (name, value, checked) => {
    setFormData(prev => ({
      ...prev,
      [name]: checked 
        ? [...prev[name], value]
        : prev[name].filter(v => v !== value)
    }));
  };

  const calculatePrice = () => {
    if (!config || !formData.number_of_students) return null;
    
    const numStudents = parseInt(formData.number_of_students) || 0;
    const basePrice = numStudents * config.per_student_price;
    
    if (numStudents >= config.discount_threshold) {
      const discount = basePrice * (config.discount_percentage / 100);
      return {
        base: basePrice,
        discount: discount,
        total: basePrice - discount,
        hasDiscount: true
      };
    }
    
    return {
      base: basePrice,
      discount: 0,
      total: basePrice,
      hasDiscount: false
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.terms_accepted) {
      toast.error('Please accept the Terms and Conditions');
      return;
    }
    if (!formData.privacy_accepted) {
      toast.error('Please accept the Privacy Policy');
      return;
    }
    if (formData.grade_levels.length === 0) {
      toast.error('Please select at least one grade level');
      return;
    }
    if (formData.preferred_days.length === 0) {
      toast.error('Please select at least one preferred day');
      return;
    }
    
    setSubmitting(true);
    
    try {
      const submitData = {
        ...formData,
        number_of_students: parseInt(formData.number_of_students)
      };
      
      const response = await schoolProgramAPI.register(submitData);
      setRegistrationResult(response.data);
      setSubmitted(true);
      toast.success('Registration submitted successfully!');
    } catch (error) {
      console.error('Registration failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit registration');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownloadFlyer = () => {
    if (config?.flyer_url) {
      window.open(config.flyer_url, '_blank');
    } else {
      toast.info('Flyer not available yet. Contact us for more information.');
    }
  };

  const nextCarouselSlide = () => {
    if (config?.carousel_images?.length) {
      setCurrentCarouselIndex(prev => (prev + 1) % config.carousel_images.length);
    }
  };

  const prevCarouselSlide = () => {
    if (config?.carousel_images?.length) {
      setCurrentCarouselIndex(prev => 
        prev === 0 ? config.carousel_images.length - 1 : prev - 1
      );
    }
  };

  const pricing = calculatePrice();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-teal-50">
        <div className="w-8 h-8 border-4 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!config?.enabled) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-teal-50 p-4">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <School className="w-16 h-16 mx-auto text-slate-400 mb-4" />
            <h2 className="text-xl font-semibold text-slate-700 mb-2">Schools Program Currently Unavailable</h2>
            <p className="text-slate-500 mb-4">Please check back later or contact us for more information.</p>
            <Button onClick={() => navigate('/')} variant="outline">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-teal-50 p-4">
        <Card className="max-w-lg">
          <CardContent className="pt-8 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-800 mb-2">Registration Submitted!</h2>
            <p className="text-slate-600 mb-6">
              Thank you for registering <strong>{formData.school_name}</strong> for our Schools Program.
            </p>
            <div className="bg-slate-50 rounded-lg p-4 mb-6 text-left">
              <p className="text-sm text-slate-600 mb-2">
                <strong>Registration ID:</strong> {registrationResult?.registration_id?.slice(0, 8).toUpperCase()}
              </p>
              <p className="text-sm text-slate-600 mb-2">
                <strong>Estimated Price:</strong> E{registrationResult?.calculated_price?.toLocaleString()}
              </p>
              <p className="text-sm text-slate-500">
                A confirmation email has been sent to {formData.contact_email}
              </p>
            </div>
            <p className="text-slate-500 text-sm mb-6">
              Our team will contact you shortly to discuss program details and scheduling.
            </p>
            <Button onClick={() => navigate('/')} className="bg-teal-600 hover:bg-teal-700">
              Return to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-teal-50">
      {/* Hero Banner with Carousel */}
      {config?.banner_enabled && (
        <div className="relative h-[300px] md:h-[400px] overflow-hidden">
          {/* Carousel Images */}
          <div className="absolute inset-0" ref={carouselRef}>
            {(config.carousel_images?.length > 0 ? config.carousel_images : [
              'https://images.pexels.com/photos/6011942/pexels-photo-6011942.jpeg'
            ]).map((img, idx) => (
              <div
                key={idx}
                className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
                  idx === currentCarouselIndex ? 'opacity-100' : 'opacity-0'
                }`}
              >
                <img 
                  src={img} 
                  alt={`Schools Program ${idx + 1}`} 
                  className="w-full h-full object-cover"
                />
              </div>
            ))}
            <div className="absolute inset-0 bg-gradient-to-r from-teal-900/90 via-teal-900/70 to-teal-800/60" />
          </div>

          {/* Carousel Controls */}
          {config.carousel_images?.length > 1 && (
            <>
              <button 
                onClick={prevCarouselSlide}
                className="absolute left-4 top-1/2 -translate-y-1/2 z-20 p-2 bg-white/20 hover:bg-white/30 rounded-full backdrop-blur-sm transition-colors"
              >
                <ChevronLeft className="w-6 h-6 text-white" />
              </button>
              <button 
                onClick={nextCarouselSlide}
                className="absolute right-4 top-1/2 -translate-y-1/2 z-20 p-2 bg-white/20 hover:bg-white/30 rounded-full backdrop-blur-sm transition-colors"
              >
                <ChevronRight className="w-6 h-6 text-white" />
              </button>
              
              {/* Carousel Dots */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-20">
                {config.carousel_images.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentCarouselIndex(idx)}
                    className={`w-2 h-2 rounded-full transition-all duration-300 ${
                      idx === currentCarouselIndex ? 'bg-white w-6' : 'bg-white/50'
                    }`}
                  />
                ))}
              </div>
            </>
          )}

          {/* Banner Content */}
          <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-4">
            <Link to="/" className="absolute top-4 left-4 inline-flex items-center text-white/80 hover:text-white">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back to Home
            </Link>
            
            <Badge className="bg-teal-500/30 text-white border-teal-400/50 mb-4">
              <School className="w-3 h-3 mr-1" /> {config.title || 'Schools Program'}
            </Badge>
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-3">
              {config.banner_title || 'Partner With Us'}
            </h1>
            <p className="text-lg md:text-xl text-teal-100 max-w-2xl mb-6">
              {config.banner_subtitle || 'Bring professional swimming instruction to your school'}
            </p>
            
            <div className="flex flex-wrap justify-center gap-3">
              <Button 
                onClick={handleDownloadFlyer}
                variant="outline" 
                className="bg-white/10 border-white/30 text-white hover:bg-white/20"
              >
                <Download className="w-4 h-4 mr-2" /> Download Flyer
              </Button>
              <Button 
                onClick={() => document.getElementById('registration-form').scrollIntoView({ behavior: 'smooth' })}
                className="bg-white text-teal-700 hover:bg-teal-50"
              >
                <FileText className="w-4 h-4 mr-2" /> Register Now
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Ad Banner */}
      {config?.ads_enabled && (
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 py-3 px-4">
          <div className="max-w-4xl mx-auto flex items-center justify-center gap-3 text-white">
            <Sparkles className="w-5 h-5 flex-shrink-0" />
            <span className="font-medium text-center">{config.ad_text || 'Special rates for schools with 20+ students!'}</span>
            <Sparkles className="w-5 h-5 flex-shrink-0 hidden sm:block" />
          </div>
        </div>
      )}

      {/* Main Content */}
      <div id="registration-form" className="py-8 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header (only show if banner is disabled) */}
          {!config?.banner_enabled && (
            <div className="text-center mb-8">
              <Link to="/" className="inline-flex items-center text-teal-600 hover:text-teal-700 mb-4">
                <ArrowLeft className="w-4 h-4 mr-1" /> Back to Home
              </Link>
              <h1 className="text-3xl font-bold text-slate-800">Schools Program Registration</h1>
              <p className="text-slate-600">Eswatini Seals Swim Academy</p>
            </div>
          )}

          {/* Highlights */}
          {config.highlights?.length > 0 && (
            <div className="mb-8">
              <div className="flex flex-wrap justify-center gap-2">
                {config.highlights.map((highlight, index) => (
                  <Badge key={index} variant="secondary" className="bg-teal-100 text-teal-700">
                    <CheckCircle className="w-3 h-3 mr-1" /> {highlight}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="grid md:grid-cols-3 gap-6">
              {/* Main Form - 2 columns */}
              <div className="md:col-span-2 space-y-6">
                {/* School Information */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <School className="w-5 h-5 text-teal-600" /> School Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="school_name">School Name *</Label>
                        <Input
                          id="school_name"
                          name="school_name"
                          value={formData.school_name}
                          onChange={handleInputChange}
                          placeholder="Enter school name"
                          required
                          data-testid="school-name-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="school_type">School Type *</Label>
                        <Select value={formData.school_type} onValueChange={(v) => handleSelectChange('school_type', v)}>
                          <SelectTrigger data-testid="school-type-select">
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                          <SelectContent>
                            {(config.school_types || ['Public', 'Private', 'Other']).map(type => (
                              <SelectItem key={type} value={type}>{type}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    <div>
                      <Label htmlFor="address">Physical Address *</Label>
                      <Input
                        id="address"
                        name="address"
                        value={formData.address}
                        onChange={handleInputChange}
                        placeholder="Full address"
                        required
                        data-testid="address-input"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="region">Region *</Label>
                      <Select value={formData.region} onValueChange={(v) => handleSelectChange('region', v)}>
                        <SelectTrigger data-testid="region-select">
                          <SelectValue placeholder="Select region" />
                        </SelectTrigger>
                        <SelectContent>
                          {config.regions?.map(region => (
                            <SelectItem key={region} value={region}>{region}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </CardContent>
                </Card>

                {/* Contact Information */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Phone className="w-5 h-5 text-teal-600" /> Contact Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="contact_person_name">Contact Person Name *</Label>
                        <Input
                          id="contact_person_name"
                          name="contact_person_name"
                          value={formData.contact_person_name}
                          onChange={handleInputChange}
                          placeholder="Full name"
                          required
                          data-testid="contact-name-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="contact_person_role">Role/Position</Label>
                        <Select value={formData.contact_person_role} onValueChange={(v) => handleSelectChange('contact_person_role', v)}>
                          <SelectTrigger data-testid="contact-role-select">
                            <SelectValue placeholder="Select role" />
                          </SelectTrigger>
                          <SelectContent>
                            {(config.contact_roles || ['Principal', 'Administrator', 'Teacher', 'Sports Coordinator', 'Other']).map(role => (
                              <SelectItem key={role} value={role}>{role}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="contact_email">Email Address *</Label>
                        <Input
                          id="contact_email"
                          name="contact_email"
                          type="email"
                          value={formData.contact_email}
                          onChange={handleInputChange}
                          placeholder="email@school.sz"
                          required
                          data-testid="contact-email-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="contact_phone">Phone Number *</Label>
                        <Input
                          id="contact_phone"
                          name="contact_phone"
                          type="tel"
                          value={formData.contact_phone}
                          onChange={handleInputChange}
                          placeholder="+268 7xxx xxxx"
                          required
                          data-testid="contact-phone-input"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Program Details */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-teal-600" /> Program Details
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="number_of_students">Number of Students *</Label>
                        <Input
                          id="number_of_students"
                          name="number_of_students"
                          type="number"
                          min="1"
                          value={formData.number_of_students}
                          onChange={handleInputChange}
                          placeholder="e.g., 30"
                          required
                          data-testid="students-count-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="program_type">Program Type *</Label>
                        <Select value={formData.program_type} onValueChange={(v) => handleSelectChange('program_type', v)}>
                          <SelectTrigger data-testid="program-type-select">
                            <SelectValue placeholder="Select program" />
                          </SelectTrigger>
                          <SelectContent>
                            {config.program_types?.map(type => (
                              <SelectItem key={type} value={type}>{type}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div>
                      <Label className="mb-2 block">Grade Levels *</Label>
                      <div className="flex flex-wrap gap-3">
                        {config.grade_levels?.map(grade => (
                          <label key={grade} className="flex items-center gap-2 cursor-pointer">
                            <Checkbox
                              checked={formData.grade_levels.includes(grade)}
                              onCheckedChange={(checked) => handleMultiSelectChange('grade_levels', grade, checked)}
                              data-testid={`grade-${grade}`}
                            />
                            <span className="text-sm">{grade}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Session Date and Time - NEW FIELDS */}
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="preferred_session_date">Preferred Start Date</Label>
                        <Input
                          id="preferred_session_date"
                          name="preferred_session_date"
                          type="date"
                          value={formData.preferred_session_date}
                          onChange={handleInputChange}
                          data-testid="session-date-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="preferred_session_time">Preferred Time Slot</Label>
                        <Select value={formData.preferred_session_time} onValueChange={(v) => handleSelectChange('preferred_session_time', v)}>
                          <SelectTrigger data-testid="session-time-select">
                            <SelectValue placeholder="Select time slot" />
                          </SelectTrigger>
                          <SelectContent>
                            {timeSlots.map(slot => (
                              <SelectItem key={slot} value={slot}>{slot}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div>
                      <Label className="mb-2 block">Preferred Days *</Label>
                      <div className="flex flex-wrap gap-3">
                        {daysOfWeek.map(day => (
                          <label key={day} className="flex items-center gap-2 cursor-pointer">
                            <Checkbox
                              checked={formData.preferred_days.includes(day)}
                              onCheckedChange={(checked) => handleMultiSelectChange('preferred_days', day, checked)}
                              data-testid={`day-${day}`}
                            />
                            <span className="text-sm">{day}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="preferred_times">Additional Time Preferences</Label>
                      <Input
                        id="preferred_times"
                        name="preferred_times"
                        value={formData.preferred_times}
                        onChange={handleInputChange}
                        placeholder="e.g., After 2pm preferred, or any specific requirements"
                        data-testid="preferred-times-input"
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Additional Options */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <MapPin className="w-5 h-5 text-teal-600" /> Additional Options
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-3">
                      <label className="flex items-center gap-3 cursor-pointer p-3 rounded-lg border hover:bg-slate-50">
                        <Checkbox
                          checked={formData.pool_access}
                          onCheckedChange={(checked) => handleCheckboxChange('pool_access', checked)}
                          data-testid="pool-access-checkbox"
                        />
                        <div>
                          <p className="font-medium">School Has Pool Access</p>
                          <p className="text-sm text-slate-500">Our instructors can come to your school's pool</p>
                        </div>
                      </label>
                      
                      <label className="flex items-center gap-3 cursor-pointer p-3 rounded-lg border hover:bg-slate-50">
                        <Checkbox
                          checked={formData.transportation_assistance}
                          onCheckedChange={(checked) => handleCheckboxChange('transportation_assistance', checked)}
                          data-testid="transportation-checkbox"
                        />
                        <div>
                          <p className="font-medium">Need Transportation Assistance</p>
                          <p className="text-sm text-slate-500">We can help arrange transport to our facilities</p>
                        </div>
                      </label>
                    </div>

                    <div>
                      <Label htmlFor="notes">Additional Notes</Label>
                      <Textarea
                        id="notes"
                        name="notes"
                        value={formData.notes}
                        onChange={handleInputChange}
                        placeholder="Any special requirements, questions, or additional information..."
                        rows={4}
                        data-testid="notes-textarea"
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Terms & Submit */}
                <Card>
                  <CardContent className="pt-6 space-y-4">
                    <div className="space-y-3">
                      <label className="flex items-start gap-3 cursor-pointer">
                        <Checkbox
                          checked={formData.terms_accepted}
                          onCheckedChange={(checked) => handleCheckboxChange('terms_accepted', checked)}
                          className="mt-1"
                          data-testid="terms-checkbox"
                        />
                        <span className="text-sm">
                          I agree to the{' '}
                          <Link to="/terms-and-conditions" target="_blank" className="text-teal-600 hover:underline">
                            Terms and Conditions
                          </Link>
                          {' '}*
                        </span>
                      </label>
                      
                      <label className="flex items-start gap-3 cursor-pointer">
                        <Checkbox
                          checked={formData.privacy_accepted}
                          onCheckedChange={(checked) => handleCheckboxChange('privacy_accepted', checked)}
                          className="mt-1"
                          data-testid="privacy-checkbox"
                        />
                        <span className="text-sm">
                          I agree to the{' '}
                          <Link to="/privacy-policy" target="_blank" className="text-teal-600 hover:underline">
                            Privacy Policy
                          </Link>
                          {' '}*
                        </span>
                      </label>
                    </div>

                    <Button
                      type="submit"
                      className="w-full bg-teal-600 hover:bg-teal-700 text-lg py-6"
                      disabled={submitting}
                      data-testid="submit-registration-btn"
                    >
                      {submitting ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                          Submitting...
                        </>
                      ) : (
                        config?.ad_cta_text || 'Submit Registration'
                      )}
                    </Button>
                  </CardContent>
                </Card>
              </div>

              {/* Pricing Sidebar */}
              <div className="md:col-span-1">
                <div className="sticky top-4 space-y-4">
                  <Card className="bg-gradient-to-br from-teal-600 to-teal-700 text-white">
                    <CardHeader>
                      <CardTitle className="text-white">Pricing Estimate</CardTitle>
                      <CardDescription className="text-teal-100">
                        E{config.per_student_price} per student
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      {pricing ? (
                        <div className="space-y-3">
                          <div className="flex justify-between text-teal-100">
                            <span>{formData.number_of_students} students</span>
                            <span>E{pricing.base.toLocaleString()}</span>
                          </div>
                          {pricing.hasDiscount && (
                            <div className="flex justify-between text-green-300">
                              <span>Bulk Discount ({config.discount_percentage}%)</span>
                              <span>-E{pricing.discount.toLocaleString()}</span>
                            </div>
                          )}
                          <div className="border-t border-teal-500 pt-3">
                            <div className="flex justify-between text-xl font-bold">
                              <span>Total</span>
                              <span>E{pricing.total.toLocaleString()}</span>
                            </div>
                          </div>
                          {parseInt(formData.number_of_students) >= config.discount_threshold && (
                            <Badge className="bg-green-500 text-white">
                              Bulk Discount Applied!
                            </Badge>
                          )}
                        </div>
                      ) : (
                        <p className="text-teal-100 text-sm">
                          Enter number of students to see pricing
                        </p>
                      )}
                      
                      {config.discount_threshold && (
                        <p className="text-teal-200 text-xs mt-4">
                          * {config.discount_percentage}% discount for {config.discount_threshold}+ students
                        </p>
                      )}
                    </CardContent>
                  </Card>

                  {/* Flyer Download Card */}
                  {config?.flyer_url && (
                    <Card className="border-2 border-dashed border-teal-300 bg-teal-50/50">
                      <CardContent className="pt-6 text-center">
                        <FileText className="w-10 h-10 text-teal-600 mx-auto mb-3" />
                        <h3 className="font-semibold mb-2">Download Program Flyer</h3>
                        <p className="text-sm text-slate-500 mb-3">
                          Get our detailed program brochure
                        </p>
                        <Button 
                          onClick={handleDownloadFlyer}
                          variant="outline" 
                          className="w-full border-teal-600 text-teal-600 hover:bg-teal-600 hover:text-white"
                        >
                          <Download className="w-4 h-4 mr-2" /> Download JPEG
                        </Button>
                      </CardContent>
                    </Card>
                  )}

                  {/* Contact Card */}
                  <Card>
                    <CardContent className="pt-6">
                      <h3 className="font-semibold mb-3">Need Help?</h3>
                      <p className="text-sm text-slate-500 mb-3">
                        Contact us for more information about our Schools Program.
                      </p>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center gap-2 text-slate-600">
                          <Mail className="w-4 h-4" />
                          <span>schools@eswatiniseals.com</span>
                        </div>
                        <div className="flex items-center gap-2 text-slate-600">
                          <Phone className="w-4 h-4" />
                          <span>+268 7xxx xxxx</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SchoolRegistration;
